import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { displayText } from "@/lib/displayText";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";

const GoogleLogo = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

export default function FindResult() {
  const navigate = useNavigate();
  const { isAuthed, user, loginGoogle, logout } = useAuth();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  useEffect(() => {
    if (!isAuthed) return;
    api.get("/auth/results").then(({ data }) => setResults(data.results)).catch(() => setResults([]));
  }, [isAuthed]);

  const search = async () => {
    if (!emailValid) return;
    setLoading(true);
    setResults(null);
    try {
      const { data } = await api.get(`/results`, { params: { email } });
      setResults(data.results);
    } catch (e) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const ResultsList = () => (
    <div className="mt-8" aria-live="polite">
      {results && results.length === 0 && <div className="rule pt-6" data-testid="find-empty"><p className="muted text-sm">Aucun résultat déverrouillé pour le moment.</p><button onClick={() => navigate("/quiz")} data-testid="find-create-business" className="btn-secondary mt-5">Créer mon business <span aria-hidden="true">→</span></button></div>}
      {results && results.map((r) => <button key={r.id} onClick={() => navigate(`/result/${r.id}`)} data-testid={`find-result-${r.id}`} className="find-result"><div><p className="font-heading">{displayText(r.business_name)}</p><p className="mono muted text-[10px] mt-2">{r.type === "physique" ? "PHYSIQUE" : "EN LIGNE"} · {new Date(r.created_at).toLocaleDateString("fr-FR")}</p></div><span className="accent" aria-hidden="true">→</span></button>)}
    </div>
  );

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="container page-content">
        <div className="fade-in lookup-layout">
          {isAuthed ? (
            <>
              <div>
                <p className="eyebrow">TON COMPTE</p>
                <h1>Mon <em>compte.</em></h1>
                <p className="muted mt-6" data-testid="account-email-display">Connecté avec {user.email}</p>
                <button onClick={logout} data-testid="logout-button" className="btn-secondary btn-small mt-6">Se déconnecter</button>
              </div>
              <div className="lookup-form">
                <p className="field-label">Tes business déverrouillés</p>
                <ResultsList />
              </div>
            </>
          ) : (
            <>
              <div><p className="eyebrow">TON PROJET, TOUJOURS LÀ</p><h1>Retrouver mon <em>résultat.</em></h1><p className="muted mt-6">Connecte-toi ou entre l'email utilisé lors de ton paiement.</p></div>
              <div className="lookup-form">
                <label className="field-label" htmlFor="find-email">Ton adresse email</label>
                <input id="find-email" type="email" autoComplete="email" data-clarity-mask="true" data-testid="find-email" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="ton@email.com" className="field" />
                <button onClick={search} disabled={!emailValid || loading} data-testid="find-button" className="btn-primary w-full mt-4">{loading ? "Recherche en cours..." : "Retrouver mon résultat"}{!loading && <span aria-hidden="true">→</span>}</button>
                <ResultsList />
              </div>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
